/**
 * Sous-app PWA 2i Recouvrement (agents recouvrement BZ).
 *
 * Scope isolé : /verif/*  — auth par code d'accès + session token 24h.
 * SW dédié `/verif-sw.js` avec scope `/verif/`.
 */

import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Accueil from './pages/Accueil';
import Fiche from './pages/Fiche';
import Onboarding from './pages/Onboarding';
import Login from './pages/Login';
import Settings from './pages/Settings';
import { getSession, getAgent } from './services/sessionApi';

// Manifest PWA dédié + swap dynamique du <link rel="manifest">.
function useVerifManifest() {
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]') as HTMLLinkElement | null;
    const previous = link?.href || '';
    if (link) link.href = '/verif-manifest.json';

    const previousTitle = document.title;
    document.title = '2i Recouvrement';

    let meta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null;
    const previousTheme = meta?.content || '';
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'theme-color';
      document.head.appendChild(meta);
    }
    meta.content = '#1e40af';

    return () => {
      if (link && previous) link.href = previous;
      if (meta && previousTheme) meta.content = previousTheme;
      document.title = previousTitle;
    };
  }, []);
}

function useVerifServiceWorker() {
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker
      .register('/verif-sw.js', { scope: '/verif/' })
      .catch(() => { /* installable même sans SW */ });
  }, []);
}

// Guard : redirige vers onboarding (1er lancement) OU login (session expirée)
const AuthGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const session = getSession();
    if (session) return;
    const agent = getAgent();
    // Pas de session : appareil déjà enregistré (agent connu) → login ;
    // sinon onboarding.
    navigate(agent ? '/verif/login' : '/verif/onboarding', { replace: true });
  }, [navigate, location.pathname]);

  const session = getSession();
  if (!session) return null;
  return <>{children}</>;
};

export default function VerificationRoutes() {
  useVerifManifest();
  useVerifServiceWorker();

  return (
    <Routes>
      <Route path="onboarding" element={<Onboarding />} />
      <Route path="login" element={<Login />} />
      <Route index element={<AuthGuard><Accueil /></AuthGuard>} />
      <Route path="fiche/:matricule" element={<AuthGuard><Fiche /></AuthGuard>} />
      <Route path="settings" element={<AuthGuard><Settings /></AuthGuard>} />
      <Route path="*" element={<Navigate to="/verif" replace />} />
    </Routes>
  );
}
